import { getTranslation } from './Translation';

/** Minne (minner.no / minnen.se) is chosen by the domain; older blocks have no domain and try Norwegian first */
export type MinneCountry = "no" | "sv";

/**
 * Convert an answer from Minne to plain paragraphs. Answers can contain HTML and entities ("H&#234;ne")
 * @param {string} html Answer from Minne
 * @returns {string[]} Paragraphs
 */
export const minneTextToParagraphs = (html?: string): string[] => {
  if (typeof html !== "string" || !html) { return []; }
  const withBreaks = html.replace(/<\/p>|<br\s*\/?>/gi, "\n");
  const text = new DOMParser().parseFromString(withBreaks, "text/html").body.textContent || "";
  return text.split(/\n+/).map(line => line.trim()).filter(Boolean);
}

/**
 * The text of an answer: the chosen option for select, radio and checkbox questions, otherwise the written answer
 * @returns {string[]} Paragraphs
 */
export const getMinneAnswer = (value: any, language?: string): string[] => {
  const type = (value?.topic_item?.type || "").toLowerCase();
  if (["select", "radio", "checkbox"].includes(type)) {
    return minneTextToParagraphs(getTranslation(value?.option_labels?.[0]?.[0], language) || "");
  }
  return minneTextToParagraphs(value?.display_value);
}

/** Date in Minne responses: an RFC date in lists, an ISO date without time zone (UTC) for single responses */
export const parseMinneDate = (date?: string): number => {
  if (!date) { return NaN; }
  return Date.parse(/T\d/.test(date) && !/Z|[+-]\d\d:?\d\d$/.test(date) ? `${date}Z` : date);
}

/** Minne's language codes for the kiosk languages that differ */
const MINNE_LANGUAGES: Record<string, string> = { no: "nb", nb_no: "nb", nn_no: "nn" };

/**
 * Get a text in the current language from Minne's *_i18n objects (nb, nn, en…)
 * @returns {string} Text, or a fallback language
 */
export const getMinneText = (i18n: any, language?: string): string => {
  if (!i18n || typeof i18n !== "object") { return typeof i18n === "string" ? i18n : ""; }
  const key = language && (MINNE_LANGUAGES[language] || language);
  return (key && i18n[key]) || getTranslation(i18n, language) || "";
}
